import { useMemo } from "react";
import createDOMPurify from "dompurify";
import { UserIcon } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import AmyBadge from "@/components/ui/amy-badge";
import FileAttachment from "../dashboard_campaign/inbox/FileAttachment";
import { CreatorAction } from "@/types/types";
// The inbox, share page and draft preview read emails through the same cleaners.
import { cleanHtml, formatPlainText } from "@/utils/emailFormatters";

interface EmailThreadProps {
  emailThread: any[];
  selectedAction: CreatorAction;
  threadLoading: boolean;
}

export default function EmailThread({
  emailThread,
  selectedAction,
  threadLoading,
}: EmailThreadProps) {
  const DOMPurify = useMemo(() => createDOMPurify(window), []);

  if (threadLoading || emailThread.length === 0) {
    return <EmailThreadSkeleton />;
  }

  return (
    <div className="overflow-y-auto">
      {emailThread.map((email, index) => {
        const isCreator = !selectedAction.social?.email?.includes(email.to);
        return (
          <div
            key={index}
            className="flex flex-col border-b last:border-b-0 p-4"
          >
            <div className="flex flex-row items-center gap-3 w-full">
              {isCreator && (
                <Avatar className="size-10">
                  <AvatarFallback className="bg-primary/10 text-primary">
                    <UserIcon className="size-5" />
                  </AvatarFallback>
                  <AvatarImage
                    src={selectedAction.avatar}
                    alt={selectedAction.username}
                  />
                </Avatar>
              )}
              {!isCreator && (
                <AmyBadge
                  className="shrink-0 h-12 font-medium"
                  imgClassName="size-10"
                />
              )}
              <div className="flex flex-col gap-1 w-full">
                <p className="font-medium text-sm">{email.from}</p>
                <p className="text-xs text-muted-foreground">{email.subject}</p>
              </div>
              <span className="text-end text-xs text-muted-foreground text-nowrap">
                {email.sent_at}
              </span>
            </div>
            <div
              translate="yes"
              className="notranslate-exempt format-prose text-sm ml-15 mt-2 overflow-auto w-auto max-w-none border-none shadow-none text-foreground/90 [&_a]:text-primary [&_a]:underline [&_a]:hover:decoration-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-3 [&_li]:mb-1 [&_img]:max-w-full [&_img]:h-auto [&_blockquote]:border-l-2 [&_blockquote]:border-muted-foreground/30 [&_blockquote]:pl-3 [&_blockquote]:text-muted-foreground"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(
                  cleanHtml(formatPlainText(email.content))
                ),
              }}
            />
            {email.attachments && email.attachments.length > 0 && (
              <div className="flex flex-wrap gap-2 ml-11 my-5">
                {email.attachments.map(
                  (attachment: any, attachmentIndex: number) => (
                    <FileAttachment
                      key={attachmentIndex}
                      attachment={attachment}
                      messageId={email.gmail_message_id || email.id}
                      campaignCreatorId={selectedAction._id}
                    />
                  )
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function EmailThreadSkeleton() {
  return (
    <div className="w-full space-y-4 overflow-hidden">
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          className="flex flex-col gap-2 pb-4 border-b last:border-b-0 p-4 w-full"
        >
          <div className="flex flex-row items-center gap-3 w-full">
            <Skeleton className="size-10 rounded-pill shrink-0" />
            <div className="flex flex-col gap-2 w-full">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-3 w-48" />
            </div>
            <Skeleton className="h-3 w-16 shrink-0" />
          </div>
          <div className="ml-12 space-y-2 mt-2 w-full">
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-[90%]" />
            <Skeleton className="h-3 w-[85%] mt-4" />
            <Skeleton className="h-3 w-[60%]" />
            <Skeleton className="h-3 w-[95%]" />
            <Skeleton className="h-3 w-[80%] mt-2" />
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-[90%]" />
            <Skeleton className="h-3 w-[85%] mt-4" />
            <Skeleton className="h-3 w-[60%]" />
            <Skeleton className="h-3 w-[95%]" />
            <Skeleton className="h-3 w-[80%] mt-2" />
          </div>
        </div>
      ))}
    </div>
  );
}
